/**
 * Alpine.js component to control TitusJS text-to-speech functionality.
 * Integrates with the BITV accessibility menu.
 *
 * Note: Due to Alpine.js interfering with private field access (#property),
 * all method calls on the Titus instance must be explicitly bound to ensure
 * proper 'this' context is maintained.
 */
import Titus from "../TitusJS/index.js";

/**
 * Default configuration for TitusJS instance
 * @type {Object}
 */
const DEFAULT_CONFIG = {
	contentSelector: "#main-content",
	autoShow: false,
	ui: {
		position: "bottom-right",
		enableHighlighting: true,
		enableBanner: true,
	},
	dom: {
		segmentSelectors: [
			"h1",
			"h2",
			"h3",
			"h4",
			"h5",
			"h6",
			"p",
			"li",
			"blockquote",
			"figcaption",
			"dd",
			"dt",
			"td",
			"th",
			".titus-content",
		],
	},
};

/**
 * TitusJS controller Alpine component
 * @returns {Object} Alpine.js component object with TitusJS integration
 */
export const titusController = () => {
	/**
	 * Component state object
	 * @type {Object}
	 */
	const component = {
		/** @type {Titus|null} TitusJS instance */
		instance: null,

		/** @type {boolean} Whether the TitusJS UI is currently active/visible */
		isActive: false,

		/** @type {boolean} Whether TitusJS has been successfully initialized */
		isInitialized: false,

		/**
		 * Initialize TitusJS instance but don't auto-show the UI
		 * @returns {Promise<void>}
		 */
		async init() {
			try {
				component.instance = new Titus(DEFAULT_CONFIG);

				// Bind the initialize method to ensure proper 'this' context with private fields
				const boundInitialize = component.instance.initialize.bind(
					component.instance,
				);
				const success = await boundInitialize();

				if (success) {
					component.isInitialized = true;

					// Set up Alpine.js state synchronization
					component._setupStateSynchronization();

					// Store instance globally for debugging
					window.titusJS = component.instance;
				} else {
					console.warn("TitusJS: Initialization failed");
				}
			} catch (error) {
				console.error("TitusJS: Initialization error:", error);
			}
		},

		/**
		 * Set up state synchronization between TitusJS and Alpine.js
		 * @private
		 */
		_setupStateSynchronization() {
			// Override the close method to sync Alpine state
			const originalClose = component.instance.close.bind(
				component.instance,
			);
			component.instance.close = () => {
				originalClose();
				component.isActive = false;
			};

			// Set up close callback to maintain original behavior
			const boundSetUICallback = component.instance.setUICallback.bind(
				component.instance,
			);
			boundSetUICallback("onClose", () => {
				component.instance.close();
			});
		},

		/**
		 * Toggle TitusJS UI visibility
		 * @returns {void}
		 */
		toggle() {
			if (!component.isInitialized) return;

			if (component.isActive) {
				component.close();
			} else {
				component.open();
			}
		},

		/**
		 * Open TitusJS UI
		 * @returns {void}
		 */
		open() {
			if (!component.isInitialized) return;

			const boundShowUI = component.instance.showUI.bind(
				component.instance,
			);
			boundShowUI();
			component.isActive = true;
		},

		/**
		 * Close TitusJS UI
		 * @returns {void}
		 */
		close() {
			if (!component.isInitialized) return;

			const boundHideUI = component.instance.hideUI.bind(
				component.instance,
			);
			boundHideUI();
			component.isActive = false;
		},

		/**
		 * Destroy TitusJS instance and clean up resources
		 * @returns {void}
		 */
		destroy() {
			if (component.instance) {
				const boundDestroy = component.instance.destroy.bind(
					component.instance,
				);
				boundDestroy();
				component.instance = null;
				component.isInitialized = false;
				component.isActive = false;

				// Clean up global reference
				if (window.titusJS) {
					delete window.titusJS;
				}
			}
		},
	};

	// Return the component object
	return component;
};
